import { useState } from 'react'
import { ChevronDown, ChevronUp, Shield, AlertTriangle, AlertCircle, Info,
         Mail, Phone, User, MapPin, Lock, FileText, Wifi, Database, Bug } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IntelligenceBriefData {
  query: string
  query_type: string
  risk_level: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  total_breaches: number
  total_records_found: number
  total_databases_searched: number
  stealer_logs_found: boolean
  stealer_count: number
  data_types_exposed: string[]
  exposed_counts: {
    emails: number
    phones: number
    persons: number
    locations: number
    passwords: number
    documents: number
    ips: number
  }
  exposed_identities: ExposedIdentity[]
  breach_list: BreachEntry[]
}

interface ExposedIdentity {
  name: string
  emails: string[]
  phones: string[]
  locations: string[]
  documents: string[]
  breach_sources: string[]
}

interface BreachEntry {
  name: string
  date: string
  description: string
  data_types: string[]
  severity: 'LOW' | 'MEDIUM' | 'HIGH'
}

// ─── Constants ────────────────────────────────────────────────────────────────

const RISK_CONFIG = {
  CRITICAL: { label: 'CRITICAL', color: 'bg-red-600 text-white border-red-500', icon: AlertCircle, bar: 'bg-red-600', ring: 'border-red-600/40' },
  HIGH:     { label: 'HIGH',     color: 'bg-red-500/20 text-red-400 border-red-500/40', icon: AlertTriangle, bar: 'bg-red-500', ring: 'border-red-500/30' },
  MEDIUM:   { label: 'MEDIUM',   color: 'bg-amber-500/20 text-amber-400 border-amber-500/40', icon: AlertTriangle, bar: 'bg-amber-500', ring: 'border-amber-500/30' },
  LOW:      { label: 'LOW',      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', icon: Info, bar: 'bg-yellow-500', ring: 'border-yellow-500/30' },
  NONE:     { label: 'CLEAN',    color: 'bg-green-500/20 text-green-400 border-green-500/40', icon: Shield, bar: 'bg-green-500', ring: 'border-green-500/30' },
}

const SEVERITY_CONFIG = {
  HIGH:   { dot: 'bg-red-500',   text: 'text-red-400',   label: 'High' },
  MEDIUM: { dot: 'bg-amber-500', text: 'text-amber-400', label: 'Med' },
  LOW:    { dot: 'bg-blue-500',  text: 'text-blue-400',  label: 'Low' },
}

const DATA_TYPE_META: Record<string, { Icon: React.ComponentType<any>, label: string, color: string }> = {
  email:    { Icon: Mail,     label: 'Emails',     color: 'text-blue-400' },
  phone:    { Icon: Phone,    label: 'Phones',     color: 'text-teal-400' },
  person:   { Icon: User,     label: 'Identities', color: 'text-purple-400' },
  location: { Icon: MapPin,   label: 'Locations',  color: 'text-green-400' },
  password: { Icon: Lock,     label: 'Passwords',  color: 'text-red-400' },
  document: { Icon: FileText, label: 'Documents',  color: 'text-amber-400' },
  ip:       { Icon: Wifi,     label: 'IPs',        color: 'text-cyan-400' },
}

const COUNT_KEY: Record<string, keyof IntelligenceBriefData['exposed_counts']> = {
  email: 'emails', phone: 'phones', person: 'persons',
  location: 'locations', password: 'passwords', document: 'documents', ip: 'ips',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatPill({ type, count }: { type: string; count: number }) {
  const meta = DATA_TYPE_META[type]
  if (!meta || count === 0) return null
  const { Icon, label, color } = meta
  return (
    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className="text-white font-mono font-semibold text-sm">{count}</span>
      <span className="text-slate-400 text-xs">{label}</span>
    </div>
  )
}

function BreachCard({ breach }: { breach: BreachEntry }) {
  const sev = SEVERITY_CONFIG[breach.severity] ?? SEVERITY_CONFIG.LOW
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0">
      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sev.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white text-sm font-medium truncate">{breach.name}</span>
          {breach.date && breach.date !== 'Unknown' && breach.date !== 'Unknown Date' && (
            <span className="text-slate-500 text-xs">{breach.date}</span>
          )}
          {breach.data_types.map(dt => (
            <span key={dt} className="text-xs px-1.5 py-0.5 bg-white/5 text-slate-400 rounded">
              {dt}
            </span>
          ))}
        </div>
        {breach.description && (
          <p className="text-slate-500 text-xs mt-0.5 line-clamp-1">{breach.description}</p>
        )}
      </div>
      <span className={`text-xs font-mono flex-shrink-0 ${sev.text}`}>{sev.label}</span>
    </div>
  )
}

function IdentityCard({ identity }: { identity: ExposedIdentity }) {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = identity.emails.length > 0 || identity.phones.length > 0 ||
                     identity.locations.length > 0 || identity.documents.length > 0

  return (
    <div className="border border-white/10 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
        onClick={() => hasDetails && setExpanded(e => !e)}
      >
        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white text-sm font-medium">{identity.name || 'Unknown'}</span>
            {identity.breach_sources.slice(0, 2).map(b => (
              <span key={b} className="text-xs px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded">
                {b}
              </span>
            ))}
            {identity.breach_sources.length > 2 && (
              <span className="text-xs text-slate-500">+{identity.breach_sources.length - 2} more</span>
            )}
          </div>
          {!expanded && (
            <div className="flex items-center gap-3 mt-0.5">
              {identity.emails[0] && <span className="text-xs text-blue-400">{identity.emails[0]}</span>}
              {identity.phones[0] && <span className="text-xs text-teal-400">{identity.phones[0]}</span>}
              {identity.locations[0] && <span className="text-xs text-slate-400">{identity.locations[0]}</span>}
            </div>
          )}
        </div>
        {hasDetails && (
          expanded
            ? <ChevronUp className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
            : <ChevronDown className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
        )}
      </button>

      {expanded && hasDetails && (
        <div className="px-4 pb-3 space-y-2 bg-black/20">
          {identity.emails.length > 0 && (
            <div className="flex gap-2">
              <Mail className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {identity.emails.map(e => (
                  <span key={e} className="text-xs text-blue-300 bg-blue-500/10 px-2 py-0.5 rounded font-mono">{e}</span>
                ))}
              </div>
            </div>
          )}
          {identity.phones.length > 0 && (
            <div className="flex gap-2">
              <Phone className="w-3.5 h-3.5 text-teal-400 mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {identity.phones.map(p => (
                  <span key={p} className="text-xs text-teal-300 bg-teal-500/10 px-2 py-0.5 rounded font-mono">{p}</span>
                ))}
              </div>
            </div>
          )}
          {identity.locations.length > 0 && (
            <div className="flex gap-2">
              <MapPin className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
              <div className="flex flex-col gap-0.5">
                {identity.locations.slice(0, 3).map(l => (
                  <span key={l} className="text-xs text-slate-300">{l}</span>
                ))}
              </div>
            </div>
          )}
          {identity.documents.length > 0 && (
            <div className="flex gap-2">
              <FileText className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {identity.documents.map(d => (
                  <span key={d} className="text-xs text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded font-mono">{d}</span>
                ))}
              </div>
            </div>
          )}
          {identity.breach_sources.length > 0 && (
            <div className="flex gap-2 pt-1 border-t border-white/5">
              <Database className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex flex-wrap gap-1">
                {identity.breach_sources.map(b => (
                  <span key={b} className="text-xs text-red-300 bg-red-500/10 px-2 py-0.5 rounded">{b}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface IntelligenceBriefProps {
  brief: IntelligenceBriefData
}

export function IntelligenceBrief({ brief }: IntelligenceBriefProps) {
  const [showBreaches, setShowBreaches] = useState(true)
  const [showIdentities, setShowIdentities] = useState(true)
  const [identityLimit, setIdentityLimit] = useState(10)

  const risk = RISK_CONFIG[brief.risk_level] ?? RISK_CONFIG.NONE
  const RiskIcon = risk.icon

  const hasIdentities = brief.exposed_identities.length > 0
  const hasBreaches   = brief.breach_list.length > 0

  return (
    <div className={`rounded-xl border ${risk.ring} bg-black/40 backdrop-blur-sm overflow-hidden`}>

      {/* ── Header bar ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-white/10">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-semibold ${risk.color}`}>
          <RiskIcon className="w-4 h-4" />
          {risk.label}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium text-sm truncate">{brief.query}</span>
            <span className="text-slate-500 text-xs capitalize">{brief.query_type}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-slate-400">
            <span>{brief.total_breaches} breach{brief.total_breaches !== 1 ? 'es' : ''}</span>
            {brief.total_records_found > 0 && <span>{brief.total_records_found.toLocaleString()} records</span>}
            {brief.total_databases_searched > 0 && <span>{brief.total_databases_searched} databases</span>}
          </div>
        </div>

        {brief.stealer_logs_found && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-900/30 border border-red-500/40 rounded-lg">
            <Bug className="w-3.5 h-3.5 text-red-400" />
            <span className="text-red-400 text-xs font-semibold">Stealer Logs</span>
          </div>
        )}
      </div>

      {/* ── Exposed data stats row ──────────────────────────────────────── */}
      {brief.data_types_exposed.length > 0 && (
        <div className="px-5 py-3 flex flex-wrap gap-2 border-b border-white/10">
          {brief.data_types_exposed.map(dt => (
            <StatPill
              key={dt}
              type={dt}
              count={brief.exposed_counts[COUNT_KEY[dt]] ?? 0}
            />
          ))}
        </div>
      )}

      {/* ── Body: two-column on wide screens ────────────────────────────── */}
      <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-white/10">

        {/* Breach list */}
        {hasBreaches && (
          <div className="flex-1 min-w-0">
            <button
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-colors"
              onClick={() => setShowBreaches(v => !v)}
            >
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-300">
                  Breach Sources
                </span>
                <span className="text-xs text-slate-500">({brief.breach_list.length})</span>
              </div>
              {showBreaches ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showBreaches && (
              <div className="px-5 pb-4 space-y-0">
                {brief.breach_list.map((b, i) => <BreachCard key={i} breach={b} />)}
              </div>
            )}
          </div>
        )}

        {/* Exposed identities */}
        {hasIdentities && (
          <div className="flex-1 min-w-0">
            <button
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/5 transition-colors"
              onClick={() => setShowIdentities(v => !v)}
            >
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-300">
                  Exposed Identities
                </span>
                <span className="text-xs text-slate-500">({brief.exposed_identities.length})</span>
              </div>
              {showIdentities ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {showIdentities && (
              <div className="px-4 pb-4 space-y-2">
                {brief.exposed_identities.slice(0, identityLimit).map((id, i) => (
                  <IdentityCard key={i} identity={id} />
                ))}
                {brief.exposed_identities.length > identityLimit && (
                  <button
                    className="w-full py-2 text-xs text-slate-400 hover:text-white border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
                    onClick={() => setIdentityLimit(l => l + 20)}
                  >
                    Show {Math.min(20, brief.exposed_identities.length - identityLimit)} more identities
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
